import { useAtom } from '@reatom/npm-react'
import { useEffect, useState } from 'react'
import { usePreviousDistinct } from 'react-use'
import styled from 'styled-components'

import { Network } from '@root/entities/network'
import { NetworkIcon } from '@root/entities/network/ui'
import { nativePriceByNetwork } from '@root/features/realtime/atoms'
import { AmountFormatted } from '@root/shared/core/format'

type StatusBarPricesProps = {}

const PriceBlock = ({ network }: { network: Network.Network }) => {
  const [price] = useAtom(nativePriceByNetwork[network])
  const previousPrice = usePreviousDistinct(
    price,
    (prev, curr) => !!prev && prev.eq(curr),
  )
  const [highlight, setHighlight] = useState(false)

  useEffect(() => {
    if (!previousPrice) {
      return
    }
    const diff = previousPrice.minus(price).abs()
    if (diff.gt(0.5)) {
      setHighlight(true)
      setTimeout(() => {
        setHighlight(false)
      }, 2000)
    }
  }, [previousPrice, price])

  if (price.lte(0)) {
    return undefined
  }

  return (
    <PriceBlockRoot data-highlight={highlight}>
      <NetworkIcon size={14} network={network} />
      <AmountFormatted amount={price} currency="$" />
    </PriceBlockRoot>
  )
}

const PriceBlockRoot = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 3px;
  align-items: center;
  color: var(--text-11);
  transition: color 0.15s linear;

  &[data-highlight='true'] {
    color: var(--amber-9);
    text-shadow: 0 0 2px var(--amberA-11);
  }

  font-size: 11px;
  font-weight: 500;
`

export const StatusBarPrices: React.FC<StatusBarPricesProps> = () => {
  return (
    <Root>
      {Network.ENABLED_NETWORKS.map((network) => (
        <PriceBlock key={network} network={network} />
      ))}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
  align-items: center;
  padding-inline: 4px;
`
